// ═══════════════════════════════════════════════════════════════════
// strikeLanguage.js — THE STRIKE LANGUAGE · H21 · Terror TCG
// A vocabulary of attacks and arrivals, for the 3D Shatterfield.
// ═══════════════════════════════════════════════════════════════════
//
// caP'n's ask (July 25, minted as H21): "can we make unique attack
// animations? Maybe a generic Lyt attack, but Imperas, Tomes etc all
// that — even the way they appear on screen, because they're
// different."
//
// caP'n's amendment (July 26): "3D only! leave the 2D alone, no
// animations at all, just 3D board."
//
// They ARE different. Terror's taxonomy is one of the richest in any
// card game — eight card types, four being Ilks, eight Tome schools,
// four Affinities — and until today every card flew in on the same
// ballistic arc and every attacker leaned the same 0.34 units across
// the seam. The taxonomy was invisible in motion. This file is the
// vocabulary that fixes it.
//
// ── WHY THIS FILE HAS NO CSS IN IT ─────────────────────────────
// The first cut of this registry emitted CSS keyframes, because the
// 2D board animates cards with CSS classes. The 3D board cannot use a
// single line of it: Shatterfield3D runs under the PERF LAW
// (ie-realtime-perf) — *no setState in useFrame, all animation is
// imperative on refs* — so every motion here is exported as PURE
// MATH: a sampler that takes a 0→1 timeline and returns a pose. A
// caller feeds it `dt` and writes the result straight onto a ref.
//
// That constraint turned out to be the better design anyway. Numbers
// port; stylesheets don't. Annihilation's dungeons and Sol Survivor's
// skill tree can consume this exact table from three.js with no DOM
// in sight, and the 2D board could still consume it later by reading
// the same samplers into inline transforms. One table, any renderer.
//
// ── THE THREE LAWS THIS SHIPS UNDER (roadmap H21) ──────────────
//
//  1. THE PURE-RENDERER CONTRACT (non-negotiable). Every profile here
//     is a RESPONSE to a state change the engine already made.
//     Nothing is awaited. Nothing gates a rule. Delete this file
//     mid-match and the duel resolves identically — it just goes
//     quiet. The board NEVER waits on an animation.
//
//  2. ONE REGISTRY, ZERO PER-CARD CODE. Everything resolves off data
//     already on the card object — `type`, `ilk`, `school`, `level`,
//     `affinityType`. Mint a 73rd Tome tomorrow and it arrives in its
//     school's language for free.
//
//  3. THE DIAL SHIPS WITH THE LANGUAGE (T17). An animation system
//     without its off-switch is a feature you can't undo. caP'n:
//     "don't want people wasting their turn watching animations xD."
//     Part I is that switch, and it is FIRST in the file on purpose.
//
// ── ONE EVENT, ONE CHANNEL (H20's discipline, honored early) ───
//     ARRIVAL — how a card ENTERS the world   (its flight + its landing)
//     STRIKE  — how a being SWINGS            (the attacker's own motion)
//     IMPACT  — how a wound LANDS             (the victim's reaction)
// Three channels, never overlapping. A card can be arriving OR
// striking — different lifecycle moments. A card can strike while
// another takes impact; that reads as two things because it IS two.
//
// ── TERMINOLOGY LAW (the vyren pattern) ────────────────────────
// The CARD IDS are still `p_*` — vyren-law, ids never change. The type
// id is not: it was `Primera` / `PRIMERAS` and is now `Impera` / `IMPERA`
// at the source, matching the canon. A Primera is the innate STAMP and
// never a card, so the two words no longer overlap and this file no
// longer has to translate between them.
//
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";

// ═══════════════════════════════════════════════════════════════════
// PART I — THE DIAL (T17)
// ═══════════════════════════════════════════════════════════════════
// Three positions, not two. The player who wants speed usually wants
// SHORTER, not ABSENT — QUICK is the position that actually gets used,
// and OFF is there so the promise is honest.
//
// T14 (accessibility reduced-motion) and T17 (impatience) read this
// same store by design, and they must not fight: OFF wins over either.
// The OS-level `prefers-reduced-motion` is a DEFAULT here, not an
// override — a player who explicitly picks FULL in-game gets FULL.
// Their machine, their call.

export const MOTION = { FULL: "full", QUICK: "quick", OFF: "off" };
export const MOTION_ORDER = [MOTION.FULL, MOTION.QUICK, MOTION.OFF];
export const MOTION_LABELS = {
  [MOTION.FULL]: "Full", [MOTION.QUICK]: "Quick", [MOTION.OFF]: "Off",
};
export const MOTION_BLURBS = {
  [MOTION.FULL]:  "Every strike, arrival and wound in full.",
  [MOTION.QUICK]: "The same language, roughly half as long.",
  [MOTION.OFF]:   "No combat motion. The board just updates.",
};

const STORE_KEY = "terror_motion";
const QUICK_FACTOR = 0.45; // dial — settle it in playtest, not in theory

let _tier;
const _subs = new Set();

function systemPrefersReduced() {
  try {
    return typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) { return false; }
}

/** The current motion tier. Reads storage once, then memoises. */
export function getMotion() {
  if (_tier !== undefined) return _tier;
  let stored = null;
  try { stored = localStorage.getItem(STORE_KEY); } catch (_) { /* private mode */ }
  _tier = (stored && MOTION_ORDER.indexOf(stored) !== -1)
    ? stored
    : (systemPrefersReduced() ? MOTION.OFF : MOTION.FULL);
  return _tier;
}

/** Set the tier, persist it, wake every subscriber. */
export function setMotion(tier) {
  if (MOTION_ORDER.indexOf(tier) === -1) return getMotion();
  _tier = tier;
  try { localStorage.setItem(STORE_KEY, tier); } catch (_) { /* private mode */ }
  _subs.forEach((fn) => { try { fn(tier); } catch (_) { /* a bad listener never breaks the board */ } });
  return _tier;
}

/** Advance the dial one notch — FULL → QUICK → OFF → FULL. */
export function cycleMotion() {
  const i = MOTION_ORDER.indexOf(getMotion());
  return setMotion(MOTION_ORDER[(i + 1) % MOTION_ORDER.length]);
}

/** Subscribe to dial changes. Returns the unsubscribe. */
export function subscribeMotion(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

/** React binding. Consumers re-render the instant the dial moves. */
export function useMotion() {
  const [tier, setTier] = useState(getMotion);
  useEffect(() => subscribeMotion(setTier), []);
  return tier;
}

/** true when ANY combat motion should play. */
export const motionOn = (tier) => (tier || getMotion()) !== MOTION.OFF;

/**
 * Scale a duration by the dial. OFF returns 0 — callers treat 0 as
 * "don't mount the effect at all", never as "play it instantly".
 */
export function scaleMs(ms, tier) {
  const t = tier || getMotion();
  if (t === MOTION.OFF) return 0;
  if (t === MOTION.QUICK) return Math.max(90, Math.round(ms * QUICK_FACTOR));
  return ms;
}

/** The timeline multiplier. QUICK runs the same curve faster. */
export function motionRate(tier) {
  const t = tier || getMotion();
  if (t === MOTION.OFF) return 0;
  if (t === MOTION.QUICK) return 1 / QUICK_FACTOR;
  return 1;
}

// ═══════════════════════════════════════════════════════════════════
// PART I.5 — THE HOLD (H23)
// ═══════════════════════════════════════════════════════════════════
// caP'n (July 27): "gamer timer stops for animations."
//
// He's right and it's not merely a courtesy — a 45-second turn that
// spends six of its seconds watching a Calamity is a turn where the
// animation TOOK something from the player, and the first thing anyone
// does about that is turn the animations off. So the clock holds.
//
// ── WHY THIS LIVES HERE AND NOT IN THE 3D FILE ─────────────────────
// Shatterfield.js owns the turn clock and must never import three.js
// to read a boolean. This module is pure math with zero renderer
// dependencies — the same reason the DIAL lives here — so both boards
// can read it for the cost of one import. fx3D and Shatterfield3D
// WRITE it; Shatterfield.js only ever READS.
//
// ── WHY IT IS SAFE IN PvP (the part that looks dangerous) ──────────
// Every score fires off a state DIFF that both clients compute from
// the same Firebase truth, so both clients hold for the same span at
// the same moment. Network skew shifts one a few hundred ms, not
// seconds. The adversary's disconnect failsafe (10s of grace past
// zero, Shatterfield.js) is the outer bound this must never cross —
// hence the cap below, deliberately set under it.
//
// ── THE CAP ────────────────────────────────────────────────────────
// A turn may only buy back so much time. Without a ceiling, a long
// interrupt chain resolving eight cards in one tick could hold a clock
// past the failsafe and hand the turn to the adversary's timeout —
// the animation system would have caused a rules event, which breaks
// the PURE-RENDERER CONTRACT in the worst possible way. 8.5s (dial):
// comfortably more than any real turn spends animating, comfortably
// less than the 10s grace.

export const HOLD_CAP_MS = 8500;

const _hold = { until: 0, spent: 0, key: null };

/**
 * Hold the turn clock for `ms`. `key` identifies the turn (any stable
 * value — turn number, activePlayer, whatever the caller has); when it
 * changes the per-turn budget resets. Extends an existing hold rather
 * than replacing it, so two chords in one tick don't cut each other
 * off. Returns the ms actually granted.
 */
export function holdFor(ms, key) {
  if (!ms || ms <= 0) return 0;
  const now = Date.now();
  if (key !== _hold.key) { _hold.key = key; _hold.spent = 0; }
  const room = Math.max(0, HOLD_CAP_MS - _hold.spent);
  const grant = Math.min(ms, room);
  if (grant <= 0) return 0;
  _hold.spent += grant;
  _hold.until = Math.max(_hold.until, now) + grant;
  return grant;
}

/** true while the board is mid-cinematic and the clock should not tick. */
export function holdActive() {
  return Date.now() < _hold.until;
}

/** Drop any hold immediately — a concede, a match end, an unmount. */
export function releaseHold() { _hold.until = 0; _hold.spent = 0; _hold.key = null; }

/** ms remaining, for chrome that wants to show the pause. */
export const holdRemaining = () => Math.max(0, _hold.until - Date.now());

// ═══════════════════════════════════════════════════════════════════
// PART I.6 — THE TRIGGER BUS (H23)
// ═══════════════════════════════════════════════════════════════════
// The transition detector can see every state change, which covers
// summons, strikes, returns and attachments. What it CANNOT see is the
// moment an effect RESOLVES — caP'n's "the animation should play once
// you use the actual effect." A Tome that draws a card and a Tome that
// gains Anima both look like "a number moved" from the outside.
//
// So the engine gets a door: any handler in Shatterfield.js may call
// fireMotion() at the exact instant it resolves an effect, and the 3D
// board (which subscribes) plays that card's score. Pure presentation
// in, nothing out — a caller that fires into a board running in 2D
// simply has no listener, and nothing happens. This is the door H23
// Ship 2 walks through; Ship 1 builds it and wires the detector-visible
// channels only.

const _fireSubs = new Set();

/** Fire a card's motion by hand. `{ cardId, channel, seat, slot }`. */
export function fireMotion(evt) {
  if (!evt || !evt.cardId) return;
  _fireSubs.forEach((fn) => { try { fn(evt); } catch (_) { /* a bad listener never breaks the duel */ } });
}

/** Subscribe. Returns the unsubscribe. */
export function subscribeMotionFire(fn) {
  _fireSubs.add(fn);
  return () => _fireSubs.delete(fn);
}

// ═══════════════════════════════════════════════════════════════════
// PART II — THE PALETTE
// ═══════════════════════════════════════════════════════════════════
// Deliberately local hex, not imported from theme.js — the Strike
// Language must drop into any IE title without dragging Terror's whole
// chrome palette behind it. Where a hue matches SCHOOL_COLORS_SD /
// TYPE_COLORS_SD it is the SAME hex on purpose: the language and the
// chrome speak one colour law. Kept in sync by hand; if theme.js moves
// a hue, move it here too.

const C = {
  space:    "#22d3ee", // SCHOOL_COLORS_SD.Space   · TYPE_COLORS_SD.Tome
  phantom:  "#c084fc", // SCHOOL_COLORS_SD.Phantom · TYPE_COLORS_SD["U'hlti"]
  dragon:   "#f87171", // SCHOOL_COLORS_SD.Dragon  · TYPE_COLORS_SD.Escendant
  time:     "#fbbf24", // SCHOOL_COLORS_SD.Time    · TYPE_COLORS_SD.Impera
  gold:     "#e8b84b",
  goldPale: "#f5d878", // TYPE_COLORS_SD.Affinity, near enough
  vellum:   "#e8d5a3",
  crimson:  "#dc2626",
  bone:     "#a8896a", // TYPE_COLORS_SD.Escling
  amethyst: "#a855f7",
  indigo:   "#818cf8", // TYPE_COLORS_SD.Prophecy
  ember:    "#fb923c", // TYPE_COLORS_SD.Curio
  jynxi:    "#2ee66b",
  white:    "#f2f6ff",
  teal:     "#5eead4",
};

// ═══════════════════════════════════════════════════════════════════
// PART III — THE ARRIVAL (H21b)
// ═══════════════════════════════════════════════════════════════════
// How a card ENTERS the world, by type. The cheaper half of H21 and by
// far the more visible: a player sees eight arrivals in their first
// match and four strikes only if the duel gets bloody.
//
// The 3D board already flew every card in — but on ONE ballistic arc
// with a hardcoded speed, whether it was an Escling or a Prophecy.
// This gives each type its own PATH, not merely its own arc height.
//
// FLIGHT STYLES — how the card crosses the gap:
//   "arc"      ballistic hop; the honest default for a body
//   "drop"     straight down from directly above, no lateral ease
//   "manifest" NO travel — it appears at the destination and swells in
//   "rise"     comes up slowly from beneath the felt
//   "skim"     low and flat, barely leaving the table
//   "still"    does not move at all; the light comes to IT
//
// Profile shape:
//   name    the canon name of the motion (Codex-ready)
//   flight  { style, arc, speed, spin, tilt, flare }
//   ring    { color, r0, r1, life } — the landing stamp on the felt
//   sigil   one glyph that blooms over the card as it lands, or null
//   color   the profile hue (ring default, sigil, accents)
//   lore    one line, because a vocabulary should be readable

export const ARRIVALS = {
  // ── ESCLING — THE SEEDING ──────────────────────────────────────
  // Primordial essence waiting to be Transcended. It doesn't arrive so
  // much as it was always going to be there. Rises from beneath the
  // felt, slow and humble, no fanfare — an Escling is a promise.
  Escling: {
    name: "The Seeding",
    flight: { style: "rise", arc: 0.0, speed: 1.4, spin: 0, tilt: 0, flare: 0.15 },
    ring: { color: C.bone, r0: 0.25, r1: 0.9, life: 0.8 },
    sigil: null, color: C.bone,
    lore: "Essence surfaces. It has not decided what it is yet.",
  },

  // ── ESCENDANT — THE DESCENT ────────────────────────────────────
  // A being does not float in. It DROPS, and the felt takes it. The
  // arc is real but the LANDING is the point — the widest ring in the
  // language and the deepest squash. Heavier at higher Level: a Lord
  // falls from higher and hits harder than a Beb (see `power`, which
  // scales arc and ring alike).
  Escendant: {
    name: "The Descent",
    flight: { style: "arc", arc: 1.15, speed: 1.75, spin: 0, tilt: 0.22, flare: 0.35 },
    ring: { color: C.crimson, r0: 0.35, r1: 1.55, life: 0.66 },
    sigil: null, color: C.crimson,
    lore: "A being takes the field the only honest way: downward.",
  },

  // ── TOME — THE CASTING ─────────────────────────────────────────
  // Not a placement — a CASTING. So it does NOT fly: spellwork does
  // not cross a table, it arrives from somewhere else. The card
  // manifests at the rift and swells into being with a rune flare, and
  // the school (Tome Ilk) recolours the flare and picks the sigil.
  // Eight schools, eight readable castings — see TOME_SCHOOLS.
  Tome: {
    name: "The Casting",
    flight: { style: "manifest", arc: 0, speed: 1.25, spin: 1.6, tilt: 0, flare: 0.9 },
    ring: { color: C.space, r0: 0.15, r1: 1.25, life: 0.9 },
    sigil: "✧", color: C.space,
    lore: "The page opens and something answers.",
  },

  // ── IMPERA — THE BRANDING ──────────────────────────────────────
  // An Impera is not played, it is TRAINED — pressed into a Soul and
  // never removed. So it comes STRAIGHT DOWN from directly above,
  // fast, with no arc and no bounce, exactly like the wax seals of
  // H22. A brand does not ease in.
  Impera: {
    name: "The Branding",
    flight: { style: "drop", arc: 0, speed: 2.6, spin: 0, tilt: 0, flare: 0.55 },
    ring: { color: C.gold, r0: 0.2, r1: 1.0, life: 0.42 },
    sigil: "◈", color: C.gold,
    lore: "Pressed into the Soul. It does not come off.",
  },

  // ── U'HLTI — THE SIGNATURE ─────────────────────────────────────
  // Twelve exist. They cost Khao AND Anima — the only attachment class
  // that charges both. The blueprint says it plainly: "it should feel
  // like a war crime." So this is the longest arrival in the language
  // by a wide margin, ceremonial rather than fast, rising rather than
  // falling, with the biggest flare and the widest ring. Nothing else
  // in the game is allowed to be this loud.
  "U'hlti": {
    name: "The Signature",
    flight: { style: "rise", arc: 0.35, speed: 0.85, spin: 0.5, tilt: 0, flare: 1.4 },
    ring: { color: C.phantom, r0: 0.3, r1: 2.4, life: 1.25 },
    sigil: "◈", color: C.phantom,
    lore: "The Lord signs its name and the field reads it.",
  },

  // ── AFFINITY — THE SEALING ─────────────────────────────────────
  // Four exist, ever. Max one per Grimoire. Khao cost zero — it costs
  // nothing but EVERYTHING BEFORE IT. And note the shape of the top of
  // the ladder: it isn't more damage, it's more DENIAL. So the
  // Affinity is the one arrival whose ring travels INWARD (r0 > r1 —
  // the sampler reads that as a contracting iris). It closes. It does
  // not radiate.
  Affinity: {
    name: "The Sealing",
    flight: { style: "drop", arc: 0, speed: 1.15, spin: 0, tilt: 0, flare: 0.7 },
    ring: { color: C.goldPale, r0: 2.2, r1: 0.32, life: 0.94 },
    sigil: "✦", color: C.goldPale,
    lore: "The alignment closes. Nothing else gets in.",
  },

  // ── CURIO — THE OFFERING ───────────────────────────────────────
  // The fourth wall. A Curio's condition is checked against the
  // PLAYER, not the board — a hat, a ring, a companion at the table.
  // So it is the one card that arrives like something SET DOWN by a
  // hand rather than summoned onto the felt: low, flat, and skimming
  // in from the player's own side.
  Curio: {
    name: "The Offering",
    flight: { style: "skim", arc: 0.18, speed: 1.6, spin: 0, tilt: -0.35, flare: 0.25 },
    ring: { color: C.ember, r0: 0.28, r1: 1.0, life: 0.7 },
    sigil: "❖", color: C.ember,
    lore: "You brought this with you. That is the whole trick.",
  },

  // ── PROPHECY — THE DECLARATION ─────────────────────────────────
  // A Prophecy is a STATED AMBITION — an alternate victory announced
  // out loud, immune to every card in the game (R74). It does not
  // arrive. It is DECLARED. The card holds perfectly still while the
  // light comes to it. The longest and stillest thing in the language,
  // because a Prophecy's whole character is that it refuses to be
  // rushed or touched.
  Prophecy: {
    name: "The Declaration",
    flight: { style: "still", arc: 0, speed: 0.7, spin: 0, tilt: 0, flare: 1.2 },
    ring: { color: C.indigo, r0: 0.4, r1: 3.2, life: 1.6 },
    sigil: "✧", color: C.indigo,
    lore: "An ambition spoken aloud. Nothing in the game may answer it.",
  },
};

// ── THE EIGHT SCHOOLS ──────────────────────────────────────────────
// A Tome's Ilk is its school of magic — eight design philosophies, not
// eight elements: economy, equipment, defense, offense, search,
// recursion, chance, and negotiation. They override the Casting's
// COLOUR and SIGIL, never its motion — a Tome must read as a Tome
// first and a Ritual second.
//
// ⚠ DISAMBIGUATION (blueprint §7): a Being's Ilk is its vessel; a
// Tome's Ilk is its school. They share a word and nothing else. This
// table is ONLY ever consulted for type "Tome".
export const TOME_SCHOOLS = {
  Construction:      { color: C.gold,     sigil: "▦", lore: "Economy. It is built, and then it stays built." },
  Treasure:          { color: C.goldPale, sigil: "✦", lore: "Equipment. It belongs to one being now." },
  Nihil:             { color: C.white,    sigil: "⊘", lore: "Defense. The effect is nullified; the fare stays paid." },
  Omnia:             { color: C.crimson,  sigil: "✖", lore: "Offense. The card is destroyed, not merely stopped." },
  "Ancient Sorcery": { color: C.amethyst, sigil: "❋", lore: "The search. Somewhere in the Grimoire, the right page." },
  Ritual:            { color: C.phantom,  sigil: "⛧", lore: "Recursion. The Necropolis is not a one-way door." },
  "Shadow Gambling": { color: C.jynxi,    sigil: "⚄", lore: "Chance. J'ynxi is watching, and she is delighted." },
  Contract:          { color: C.ember,    sigil: "⚖", lore: "Negotiation. A legally binding offer to your enemy." },
};

// ── THE FOUR ALIGNMENTS ────────────────────────────────────────────
// All four Affinities are denial effects, so they share the closing
// iris; only the hue and the mark differ.
export const AFFINITY_TONES = {
  Holy:     { color: C.goldPale, sigil: "✦", lore: "Y'Tyranuz. The attack simply does not happen." },
  Evil:     { color: C.crimson,  sigil: "✖", lore: "X'aol. Something goes to the Necropolis." },
  Awoken:   { color: C.teal,     sigil: "◉", lore: "Enlightenment. It answers the answer." },
  "J'ynxi": { color: C.jynxi,    sigil: "⚄", lore: "Fiend or Faux. Never the same trick twice in a row." },
};

// ── THE DEPARTURE ──────────────────────────────────────────────────
// Not every flight is an arrival. A card falling to the Necropolis is
// going the OTHER way, and it must not wear the language of taking the
// field. One profile, deliberately drab — a descent is a debt.
export const DEPARTURE = {
  name: "The Descent to the Necropolis",
  flight: { style: "arc", arc: 0.7, speed: 1.5, spin: 0, tilt: 0.5, flare: 0 },
  ring: { color: "#a78bfa", r0: 0.3, r1: 1.1, life: 0.7 },
  sigil: null, color: "#a78bfa",
  lore: "It broke where it stood.",
};

// ── THE DRAWING ────────────────────────────────────────────────────
// A card off the Grimoire or Luxmoire isn't entering the WORLD, it's
// entering a HAND. Quick, high, unremarkable on purpose — a draw
// happens several times a turn and must never demand attention.
export const DRAWING = {
  name: "The Drawing",
  flight: { style: "arc", arc: 1.35, speed: 1.85, spin: 0, tilt: 0, flare: 0 },
  ring: { color: C.gold, r0: 0.25, r1: 0.75, life: 0.5 },
  sigil: null, color: C.gold,
  lore: "The Grimoire gives one up.",
};

// ═══════════════════════════════════════════════════════════════════
// PART IV — THE STRIKE (H21a)
// ═══════════════════════════════════════════════════════════════════
// How a being SWINGS, by Ilk.
//
// ⚠ WHAT THE 3D BOARD ALREADY HAD, AND WHAT IT DIDN'T. Card3D has
// carried a `lunge` prop since Ship 6 — but that is the SUSTAINED lean
// of a *declared* attacker choosing its target, not the swing. The
// actual moment of violence — `attackedThisTurn` flipping false→true —
// had no motion at all on this board. So the strike is a genuinely new
// channel here, and it composes into Card3D's ONE WRITER position.set
// as another TERM, never another branch. (That law is written above
// the composition in Card3D and it is not optional: three independent
// writers on one transform is three bugs.)
//
// ⚠ WHICH AXIS THE STRIKE KEYS ON. The four elemental identities the
// strike language is built out of — Space, Phantom, Dragon, Time — are
// the SCHOOL axis, so `strikeFor`/`impactFor` read `card.school`. Until
// 2026-08-25 those same four words were stored under `card.ilk`, and
// the note that stood here explained why this file read the "wrong"
// field on purpose. The full-sweep rename it said would be needed
// happened that day; the words never moved, only their label did, and
// every profile below matches the same beings it always did.
//
// Each `pose(t, p)` takes t in 0→1 and p = the being's Level (1–3) and
// returns the pose to ADD to the card's resting transform:
//   fwd    toward the enemy seat (seat-local −z serves BOTH chairs,
//          because the opponent's group is π about Y — one number,
//          two seats, which is exactly why Ship 6 built it this way)
//   up     lift off the felt
//   lat    lateral drift
//   pitch  lean, in radians
//   scale  1 = untouched
//   bright emissive kick, 0–1
//   alpha  1 = solid (Phantom is the only profile that ever dips)

const ease = {
  out:  (t) => 1 - Math.pow(1 - t, 3),
  bump: (t) => Math.sin(t * Math.PI),        // 0 → 1 → 0
  snap: (t) => Math.sin(t * Math.PI * 0.5),  // fast out, then holds
};

export const STRIKES = {
  // ── SPACE — THE VECTOR ─────────────────────────────────────────
  // Space collapses distance. There is no wind-up because there is no
  // travel — the being is simply already there, then already back.
  // Fastest strike in the language and the only one that goes out on a
  // hard linear ramp instead of an ease.
  Space: {
    name: "The Vector", ms: 340, color: C.space,
    accent: "lance", shake: 0,
    lore: "No approach. The distance was never the point.",
    pose: (t, p) => {
      const out = t < 0.34 ? t / 0.34 : Math.max(0, 1 - (t - 0.34) / 0.66);
      return {
        fwd: out * 0.62 * (0.75 + p * 0.12), up: out * 0.10,
        lat: 0, pitch: -out * 0.12, scale: 1 - out * 0.04,
        bright: out, alpha: 1,
      };
    },
  },

  // ── PHANTOM — THE FADE ─────────────────────────────────────────
  // A Phantom does not cross the field; it stops being here and starts
  // being there. It dissolves at the wind-up and re-forms displaced —
  // you never see the middle of the attack, because there isn't one.
  // The only profile in the language that touches alpha.
  Phantom: {
    name: "The Fade", ms: 560, color: C.phantom,
    accent: "motes", shake: 0,
    lore: "It was not there. Then the wound was.",
    pose: (t, p) => {
      const gone = t < 0.2 ? t / 0.2 : t < 0.5 ? 1 : Math.max(0, 1 - (t - 0.5) / 0.22);
      const travel = ease.bump(Math.min(1, t / 0.85));
      return {
        fwd: travel * 0.70 * (0.75 + p * 0.12), up: travel * 0.16,
        lat: Math.sin(t * Math.PI * 2) * 0.08, pitch: -travel * 0.16,
        scale: 1 + travel * 0.05, bright: (1 - gone) * travel,
        alpha: 1 - gone * 0.88,
      };
    },
  },

  // ── DRAGON — THE MAUL ──────────────────────────────────────────
  // The heaviest thing in the language. A long wind-up BACKWARD — real
  // anticipation, the only strike that pulls away before it commits,
  // so you get to watch it decide — then a slam with a hard landing.
  // The only strike allowed to kick the world, and only at Lord weight
  // (see `shake` plus the power gate in strikeFor).
  Dragon: {
    name: "The Maul", ms: 680, color: C.dragon,
    accent: "rake", shake: 0.075,
    lore: "It winds up. You get to watch it decide.",
    pose: (t, p) => {
      if (t < 0.42) {                      // the pull-back
        const k = ease.out(t / 0.42);
        return { fwd: -k * 0.26, up: -k * 0.04, lat: 0, pitch: k * 0.20,
                 scale: 1 - k * 0.05, bright: k * 0.25, alpha: 1 };
      }
      const k = (t - 0.42) / 0.58;
      const swing = k < 0.35 ? ease.snap(k / 0.35) : Math.max(0, 1 - (k - 0.35) / 0.65);
      return {
        fwd: -0.26 + swing * 1.05 * (0.7 + p * 0.14),
        up: swing * 0.26, lat: 0, pitch: 0.20 - swing * 0.46,
        scale: 1 + swing * 0.10, bright: swing, alpha: 1,
      };
    },
  },

  // ── TIME — THE ECHO ────────────────────────────────────────────
  // A Time being strikes before it moves. Three strobed lunges fire in
  // sequence and the body catches up to the last of them — the attack
  // reads as having already happened by the time it does. Built as a
  // sawtooth so it reads as STROBE rather than motion blur.
  Time: {
    name: "The Echo", ms: 500, color: C.time,
    accent: "echo", shake: 0,
    lore: "Three of them swing. Only one of them is now.",
    pose: (t, p) => {
      const beat = Math.min(2, Math.floor(t * 3));
      const local = (t * 3) % 1;
      const amp = [0.34, 0.52, 1.0][beat];
      const out = ease.bump(local);
      return {
        fwd: out * 0.72 * amp * (0.75 + p * 0.12), up: out * 0.14 * amp,
        lat: 0, pitch: -out * 0.18 * amp, scale: 1 + out * 0.06 * amp,
        bright: out * amp, alpha: 1,
      };
    },
  },
};

// The baseline every being inherits when its Ilk isn't one of the four
// — Esclings, and anything minted later that hasn't picked a side.
// This is Terror's original lunge, preserved as the honest floor of
// the language rather than deleted.
export const STRIKE_BASE = {
  name: "The Lunge", ms: 380, color: C.vellum,
  accent: null, shake: 0,
  lore: "The oldest motion on the board. Still honest.",
  pose: (t, p) => {
    const out = ease.bump(t);
    return {
      fwd: out * 0.58 * (0.8 + p * 0.1), up: out * 0.20,
      lat: 0, pitch: -out * 0.22, scale: 1 + out * 0.05,
      bright: out * 0.6, alpha: 1,
    };
  },
};

// ═══════════════════════════════════════════════════════════════════
// PART V — THE IMPACT
// ═══════════════════════════════════════════════════════════════════
// How a wound LANDS, by the victim's Ilk.
//
// Canon already ruled the colour: "every hit carries the victim's
// blood-hue" (the 2D board's Last Rites pass). That stays. What's new
// is that the SHAPE of the reaction is the victim's too — a Dragon
// gets rocked, a Phantom barely registers it, a Space being takes one
// clean surgical jolt, a Time being shudders in receding echoes.
//
// ⚠ WHY THE VICTIM AND NOT THE ATTACKER. The attacker's identity is
// already fully expressed in its STRIKE, which it renders itself and
// knows perfectly. The victim's card cannot see who hit it without new
// plumbing — and that plumbing would have to survive the PvP
// round-trip to fire on both clients. Keying the wound to the victim
// keeps the whole system derivable from one card's own state diff,
// which means it fires identically on every client for free. Attacker
// reads in the motion, victim reads in the wound. Clean split, no bus,
// no writes. (V'ael's call, reversible: an `attackerIlk` field on the
// shake entry would flip it.)
//
//   ms     how long the flinch runs
//   mag    jitter amplitude in world units
//   freq   jitter rate — higher reads sharper, lower reads heavier
//   sparks how many shards the wound throws (0 = none)

export const IMPACTS = {
  Space:   { name: "The Lance",      color: C.space,   ms: 420, mag: 0.045, freq: 78, sparks: 4, lore: "Surgical. One line, edge to edge." },
  Phantom: { name: "The Nicks",      color: C.phantom, ms: 520, mag: 0.028, freq: 44, sparks: 7, lore: "Scattered, and already fading." },
  Dragon:  { name: "The Rake",       color: C.dragon,  ms: 480, mag: 0.075, freq: 52, sparks: 9, lore: "Three claws. The only wound with width." },
  Time:    { name: "The Recurrence", color: C.time,    ms: 560, mag: 0.050, freq: 63, sparks: 6, lore: "One cut, three phases, receding." },
};

export const IMPACT_BASE = {
  name: "The Cut", color: C.crimson, ms: 460, mag: 0.050, freq: 66, sparks: 5,
  lore: "The oldest wound on the board.",
};

// ═══════════════════════════════════════════════════════════════════
// PART VI — THE RESOLVERS
// ═══════════════════════════════════════════════════════════════════
// Every consumer calls these and nothing else. They are TOTAL: a null
// card, an unknown type, a card minted next year — all return a usable
// profile. An animation registry that can throw is a registry that can
// take the board down with it, and this one runs inside a useFrame.

/** Level → weight, 1..3. Lords land harder and swing further. */
export function powerOf(card) {
  const lv = card && card.level;
  if (typeof lv !== "number") return 1;
  return Math.max(1, Math.min(3, lv));
}

/**
 * How this card ENTERS. School/alignment overrides already folded in.
 * `intent` lets a caller ask for a non-arrival flight instead:
 *   "play" (default) · "draw" · "depart"
 */
export function arrivalFor(card, intent) {
  if (intent === "draw")   return { ...DRAWING, power: 1 };
  if (intent === "depart") return { ...DEPARTURE, power: powerOf(card) };

  const base = (card && ARRIVALS[card.type]) || ARRIVALS.Escendant;
  let color = base.color;
  let sigil = base.sigil;
  let lore  = base.lore;

  if (card && card.type === "Tome") {
    const school = TOME_SCHOOLS[card.ilk];
    if (school) { color = school.color; sigil = school.sigil; lore = school.lore; }
  } else if (card && card.type === "Affinity") {
    const tone = AFFINITY_TONES[card.affinityType];
    if (tone) { color = tone.color; sigil = tone.sigil; lore = tone.lore; }
  }

  const power = powerOf(card);
  return {
    ...base, color, sigil, lore, power,
    // A Lord arrives from higher than a Beb. Only the ballistic style
    // scales — spellwork and declarations have no body weight to scale
    // WITH, and a Prophecy that fell harder would be nonsense.
    flight: base.flight.style === "arc"
      ? { ...base.flight, arc: base.flight.arc * (0.8 + power * 0.14) }
      : base.flight,
    // The ring inherits the school/alignment hue whenever the base
    // profile hadn't already picked a different one on purpose.
    ring: { ...base.ring, color: base.ring.color === base.color ? color : base.ring.color },
  };
}

/** How this being SWINGS. */
export function strikeFor(card) {
  const base = (card && STRIKES[card.school]) || STRIKE_BASE;
  const power = powerOf(card);
  return {
    ...base, power,
    // Only a Lord-weight Dragon may kick the whole world. Anything
    // less and the board shakes constantly, which reads as jitter
    // rather than weight.
    shake: power >= 3 ? base.shake : 0,
  };
}

/** How a wound on this card LANDS. */
export function impactFor(card) {
  const base = (card && IMPACTS[card.school]) || IMPACT_BASE;
  return { ...base, power: powerOf(card) };
}

/**
 * Sample a strike at time `t` (0→1). Total, clamped, and it never
 * returns undefined — this runs inside useFrame, where a thrown error
 * kills the whole render loop, not just one animation.
 */
export function strikePose(profile, t, power) {
  const p = profile || STRIKE_BASE;
  const tt = t <= 0 ? 0 : t >= 1 ? 1 : t;
  try {
    const r = p.pose(tt, power || p.power || 1);
    return {
      fwd: r.fwd || 0, up: r.up || 0, lat: r.lat || 0,
      pitch: r.pitch || 0, scale: r.scale == null ? 1 : r.scale,
      bright: r.bright || 0, alpha: r.alpha == null ? 1 : r.alpha,
    };
  } catch (_) {
    return { fwd: 0, up: 0, lat: 0, pitch: 0, scale: 1, bright: 0, alpha: 1 };
  }
}

// ═══════════════════════════════════════════════════════════════════
// PART VII — THE CODEX VIEW
// ═══════════════════════════════════════════════════════════════════
// The whole vocabulary as one readable table, so a Rulebook chapter, a
// settings preview or a debug screen can list the language without
// re-deriving it. Nothing in the duel path reads this.

export function describeLanguage() {
  const rows = [];
  Object.keys(ARRIVALS).forEach((type) => {
    const p = ARRIVALS[type];
    rows.push({ channel: "ARRIVAL", key: type, name: p.name, color: p.color, lore: p.lore });
  });
  Object.keys(TOME_SCHOOLS).forEach((s) => {
    const p = TOME_SCHOOLS[s];
    rows.push({ channel: "SCHOOL", key: s, name: `The Casting · ${s}`, color: p.color, lore: p.lore });
  });
  Object.keys(AFFINITY_TONES).forEach((a) => {
    const p = AFFINITY_TONES[a];
    rows.push({ channel: "ALIGNMENT", key: a, name: `The Sealing · ${a}`, color: p.color, lore: p.lore });
  });
  Object.keys(STRIKES).forEach((i) => {
    const p = STRIKES[i];
    rows.push({ channel: "STRIKE", key: i, name: p.name, color: p.color, lore: p.lore });
  });
  rows.push({ channel: "STRIKE", key: "—", name: STRIKE_BASE.name, color: STRIKE_BASE.color, lore: STRIKE_BASE.lore });
  Object.keys(IMPACTS).forEach((i) => {
    const p = IMPACTS[i];
    rows.push({ channel: "IMPACT", key: i, name: p.name, color: p.color, lore: p.lore });
  });
  rows.push({ channel: "IMPACT", key: "—", name: IMPACT_BASE.name, color: IMPACT_BASE.color, lore: IMPACT_BASE.lore });
  return rows;
}

const strikeLanguage = {
  MOTION, MOTION_ORDER, MOTION_LABELS, MOTION_BLURBS,
  getMotion, setMotion, cycleMotion, subscribeMotion, useMotion,
  motionOn, scaleMs, motionRate,
  // H23 — the hold and the door
  HOLD_CAP_MS, holdFor, holdActive, releaseHold, holdRemaining,
  fireMotion, subscribeMotionFire,
  ARRIVALS, TOME_SCHOOLS, AFFINITY_TONES, DEPARTURE, DRAWING,
  STRIKES, STRIKE_BASE, IMPACTS, IMPACT_BASE,
  arrivalFor, strikeFor, impactFor, strikePose, powerOf,
  describeLanguage,
};
export default strikeLanguage;
